import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCountries } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const RadioContext = createContext(null);

export const mediaSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function buildTracks(countries, filter = "all") {
  const list = [];
  countries.forEach((c) => {
    if (c.song && filter !== "modern") list.push({ ...c.song, kind: "classic", country: c, id: `${c.code}-classic` });
    if (c.song_modern && filter !== "classic") list.push({ ...c.song_modern, kind: "modern", country: c, id: `${c.code}-modern` });
  });
  return list;
}

const INITIAL = { filter: "all", shuffle: false, current: null, playing: false };

function reducer(state, action) {
  switch (action.type) {
    case "filter": return { ...state, filter: action.filter };
    case "shuffle": return { ...state, shuffle: !state.shuffle };
    case "start": return { ...state, current: action.track, playing: true };
    case "load": return state.current ? state : { ...state, current: action.track, playing: false };
    case "playing": return { ...state, playing: action.playing };
    case "stop": return { ...state, current: null, playing: false };
    default: return state;
  }
}

function useRadioActions(state, dispatch, tracks, countries, audioRef) {
  const { current, playing, shuffle } = state;
  return useMemo(() => {
    const resume = () => {
      audioRef.current?.play().then(() => { dispatch({ type: "playing", playing: true }); trackEvent("radio_play", { track: current?.id }); }).catch(() => {});
    };
    const pause = () => { audioRef.current?.pause(); dispatch({ type: "playing", playing: false }); };
    const start = (track) => dispatch({ type: "start", track });
    const playTrack = (tr) => { if (tr) (current && tr.id === current.id ? resume() : start(tr)); };
    const go = (dir) => {
      if (!tracks.length) return;
      const i = current ? tracks.findIndex((t) => t.id === current.id) : -1;
      start(tracks[shuffle ? Math.floor(Math.random() * tracks.length) : (i + dir + tracks.length) % tracks.length]);
    };
    return {
      resume, pause, playTrack, go,
      setFilter: (filter) => dispatch({ type: "filter", filter }),
      setShuffle: () => dispatch({ type: "shuffle" }),
      toggle: () => (playing ? pause() : resume()),
      stop: () => { pause(); dispatch({ type: "stop" }); trackEvent("radio_close"); },
      loadTrack: (track) => dispatch({ type: "load", track }),
      playById: (id) => { const tr = buildTracks(countries).find((t) => t.id === id); playTrack(tr); return Boolean(tr); },
    };
  }, [current, playing, shuffle, tracks, countries, dispatch, audioRef]);
}

function useMediaSession(current, actions) {
  useEffect(() => {
    if (!current || !("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: current.title, artist: current.artist, album: "Radio Atlante",
      artwork: current.artwork ? [{ src: current.artwork, sizes: "300x300", type: "image/jpeg" }] : [],
    });
    navigator.mediaSession.setActionHandler("play", actions.resume);
    navigator.mediaSession.setActionHandler("pause", actions.pause);
    navigator.mediaSession.setActionHandler("nexttrack", () => actions.go(1));
    navigator.mediaSession.setActionHandler("previoustrack", () => actions.go(-1));
  }, [current, actions]);
}

/** Loads the <audio> source whenever the current track changes and auto-plays if requested. */
function useAudioSource(audioRef, current, playing, dispatch, setProgress) {
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    setProgress(0);
    el.src = mediaSrc(current.preview_url);
    el.load();
    if (playing) el.play().then(() => trackEvent("radio_play", { track: current.id })).catch(() => dispatch({ type: "playing", playing: false }));
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}

const FADE_OUT_S = 2.5;
const FADE_IN_S = 1.2;

/** Smooth volume ramps at the start/end of each preview + warm the cache for the next track. */
function useCrossfade(audioRef, playing, tracks, index) {
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !playing) return;
    const id = setInterval(() => {
      const dur = el.duration || 30;
      const remaining = dur - el.currentTime;
      el.volume = Math.max(0.05, Math.min(1, el.currentTime / FADE_IN_S, remaining / FADE_OUT_S));
    }, 60);
    return () => { clearInterval(id); el.volume = 1; };
  }, [audioRef, playing]);

  useEffect(() => {
    const next = tracks[(index + 1) % tracks.length];
    if (index < 0 || !next?.preview_url) return;
    const warm = new Audio();
    warm.preload = "auto";
    warm.src = mediaSrc(next.preview_url);
  }, [tracks, index]);
}

export function RadioProvider({ children }) {
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const tracks = useMemo(() => buildTracks(countries, state.filter), [countries, state.filter]);
  const actions = useRadioActions(state, dispatch, tracks, countries, audioRef);
  useAudioSource(audioRef, state.current, state.playing, dispatch, setProgress);
  useMediaSession(state.current, actions);

  const value = useMemo(() => ({
    ...state, ...actions, tracks, progress,
    index: state.current ? tracks.findIndex((t) => t.id === state.current.id) : -1,
  }), [state, actions, tracks, progress]);

  useCrossfade(audioRef, state.playing, tracks, value.index);

  return (
    <RadioContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="auto"
        onTimeUpdate={(e) => setProgress((e.target.currentTime / (e.target.duration || 30)) * 100)}
        onEnded={() => actions.go(1)}
        data-testid="radio-audio"
      />
    </RadioContext.Provider>
  );
}

export const useRadio = () => useContext(RadioContext);
